import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./AssignUserToPhoneModal.module.scss";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import type { IOrganizationStore } from "../../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../../services/IOrganizationStore";
import type { SFUserSchema } from "@whatslly/types/analytics/AnalyticsSFTypes";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";

const getUserOptionLabel = (user: SFUserSchema & { _id: string }, _id: string) => {
  const name = user?.name ?? _id;

  return `${name}${user?.email ? ` (${user.email})` : ""}`;
};

export const AssignUserToPhoneModal: React.FC<
  IModalInstance<{ selectedUserIds: string[]; sms: boolean; voice: boolean }> & {
    initUsers: { allowedCapabilities: { sms: boolean; voice: boolean }; userId: string }[];
    capabilities: { mms: boolean; sms: boolean; voice: boolean };
  }
> = observer(({ closeModal, initUsers, capabilities }) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(initUsers.map((user) => user.userId));
  const [voicePermission, setVoice] = useState<boolean>(initUsers[0]?.allowedCapabilities.voice ?? false);
  const [smsPermission, setSms] = useState<boolean>(initUsers[0]?.allowedCapabilities.sms ?? false);
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const { t } = useTranslation();

  return (
    <BaseModal
      header={<ModalHeader closeModal={() => closeModal()}>{t("whatsapp_business_api_settings_page.actions_phone_table.assign_users")}</ModalHeader>}
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={() => closeModal()}
      className={styles.modal}
      bodyClassName={styles.modalBody}
    >
      <AsyncSelect
        loadOptionsFromServerFunction={async (inputValue) => {
          const users = await organizationStore.usersList;
          const options = users
            .filter((user) => user.isActive && user.name?.toLowerCase().includes(inputValue.toLowerCase()))
            .map((user) => {
              return { label: `${user.name || user.userId}${user.email ? ` (${user.email})` : ""}`, value: user._id };
            });
          return { options };
        }}
        data-testid="select_user_to_phone_dropdown"
        isSearchable={false}
        isMulti={true}
        value={organizationStore.usersList.case({
          fulfilled: (users) => {
            return selectedUserIds.map((_id) => {
              const user = users.find((user) => user._id === _id)!;
              return {
                label: getUserOptionLabel(user, _id),
                value: _id,
              };
            });
          },
          pending: () => [],
          rejected: () => [],
        })}
        onChange={(val: { label: string; value: string }[]) => setSelectedUserIds(val.map((v) => v.value))}
      />
      {capabilities.voice && (
        <CheckBox
          text={t("twilio_settings_page.cols.voice")}
          checked={voicePermission}
          onChange={(e) => setVoice(e.target.checked)}
          data-testid={"assign-user-to-phone-voice-check-box"}
        />
      )}
      {capabilities.sms && (
        <CheckBox
          text={t("twilio_settings_page.cols.sms")}
          checked={smsPermission}
          onChange={(e) => setSms(e.target.checked)}
          data-testid={"assign-user-to-phone-sms-check-box"}
        />
      )}
      <ModalButtons
        testId="modal-buttons-submit-button"
        onCancel={() => closeModal(undefined)}
        onSubmit={() => closeModal({ selectedUserIds, sms: smsPermission, voice: voicePermission })}
      />
    </BaseModal>
  );
});
